// Travel Budget Tracker Form - Bulk Entry
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { DollarSign, Plus, X } from 'lucide-react';

interface TravelBudgetFormProps {
  onAdd: (item: { title: string; dueDate?: string; notes?: string; metadata?: any }) => void;
}

interface ExpenseItem {
  id: string;
  expenseName: string;
  amount: string;
}

export default function TravelBudgetForm({ onAdd }: TravelBudgetFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<ExpenseItem[]>([
    { id: '1', expenseName: '', amount: '' }
  ]);

  const handleAddRow = () => {
    if (items.length >= 30) return;
    setItems([...items, { id: Date.now().toString(), expenseName: '', amount: '' }]);
  };

  const handleRemoveRow = (id: string) => {
    if (items.length === 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id: string, field: 'expenseName' | 'amount', value: string) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const validItems = items.filter(item => item.expenseName.trim() && item.amount);
    
    if (validItems.length === 0) return;

    validItems.forEach(item => {
      const amountNum = parseFloat(item.amount);
      onAdd({
        title: `💰 ${item.expenseName.trim()} - $${amountNum.toFixed(2)}`,
        metadata: {
          type: 'travel_expense',
          amount: amountNum
        }
      });
    });

    setItems([{ id: Date.now().toString(), expenseName: '', amount: '' }]);
    setIsOpen(false);
  };

  const totalAmount = items
    .filter(i => i.amount && !isNaN(parseFloat(i.amount)))
    .reduce((sum, i) => sum + parseFloat(i.amount), 0);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="w-full md:w-auto px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
      >
        <DollarSign className="w-5 h-5" />
        Add Expenses
      </button>

      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Travel Budget Items"
        maxWidth="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-slate-400">
            Add up to 30 expenses at once. Fill in expense names and amounts.
          </p>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            {items.map((item, index) => (
              <div key={item.id} className="flex gap-2 items-start">
                <div className="flex-shrink-0 w-8 pt-3 text-sm text-slate-500 font-semibold">
                  {index + 1}.
                </div>

                <div className="flex-1">
                  <input
                    type="text"
                    value={item.expenseName}
                    onChange={(e) => handleUpdateItem(item.id, 'expenseName', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-base"
                    placeholder="Expense name"
                    autoFocus={index === 0}
                  />
                </div>

                <div className="w-32">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.amount}
                    onChange={(e) => handleUpdateItem(item.id, 'amount', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-base"
                    placeholder="Amount"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveRow(item.id)}
                  disabled={items.length === 1}
                  className="flex-shrink-0 p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove expense"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          {items.length < 30 && (
            <button
              type="button"
              onClick={handleAddRow}
              className="w-full py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Another Expense ({items.length}/30)
            </button>
          )}

          <div className="flex items-center justify-between text-sm bg-slate-800/50 rounded-lg p-3">
            <span className="text-slate-400">
              {items.filter(i => i.expenseName.trim() && i.amount).length} expense(s) ready to add
            </span>
            <span className="text-emerald-300 font-semibold text-base">
              Total: ${totalAmount.toFixed(2)}
            </span>
          </div>

          <button
            type="submit"
            disabled={items.filter(i => i.expenseName.trim() && i.amount).length === 0}
            className="w-full py-4 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DollarSign className="w-5 h-5" />
            Add {items.filter(i => i.expenseName.trim() && i.amount).length} Expense(s)
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
